import React, { useCallback, useState } from "react";
import { useCookies } from "react-cookie";
import { trim } from "lodash";
import AuthenticationResponse from "../../../models/interfaces/authentication-response";
import AuthenticationContext, { AuthenticationContextProps } from "./authentication-context.ts";
import ENDPOINTS from "../../../services/endpoints";
import { fetchItem } from "../../../queries/queries-helper.ts";

const AuthenticationContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [authenticationResponse, setAuthenticationResponse] =
        useState<AuthenticationResponse | null>(null);

    const [, removeCookie] = useCookies([import.meta.env.VITE_COOKIE_NAME]);

    const setAuthenticationCallback = useCallback((value: AuthenticationResponse | null) => {
        // Les cookies sont "set" depuis le serveur.

        if (value?.organisation?.isPromoter === false) {
            window.location.href = `${import.meta.env.VITE_AM_URL}/User/Details?userId=${value.user.id}`;
            return;
        }

        setAuthenticationResponse(value);
    }, []);

    const resetAuthenticationContext = useCallback(() => {
        removeCookie(import.meta.env.VITE_COOKIE_NAME, { sameSite: 'none', secure: true, domain: import.meta.env.VITE_COOKIE_DOMAIN })
        removeCookie(`${import.meta.env.VITE_COOKIE_NAME}C1`, { sameSite: 'none', secure: true, domain: import.meta.env.VITE_COOKIE_DOMAIN })
        removeCookie(`${import.meta.env.VITE_COOKIE_NAME}C2`, { sameSite: 'none', secure: true, domain: import.meta.env.VITE_COOKIE_DOMAIN })

        setAuthenticationResponse(null)
    }, [removeCookie, setAuthenticationResponse]);

    const refetchCookieSession = useCallback(async () => {
        if (authenticationResponse != null)
            return true;

        const data = await fetchItem<AuthenticationResponse>(ENDPOINTS.LOGIN_COOKIE);
        if (data == null)
            return false;

        if (data.organisation?.isPromoter === false) {
            window.location.href = `${import.meta.env.VITE_AM_URL}/User/Details?userId=${data.user.id}`;
            return false;
        }

        setAuthenticationResponse(data);
        return true;
    }, [authenticationResponse]);

    const hasLicencePermission = useCallback((permission: string): boolean => {
        if (!authenticationResponse) return false;

        return authenticationResponse.licencePermissions.split(",").some(p => trim(p) === permission);
    }, [authenticationResponse]);

    const authenticationContextValue: AuthenticationContextProps = {
        authenticationResponse: authenticationResponse,
        setAuthenticationResponse: setAuthenticationCallback,
        resetAuthenticationContext: resetAuthenticationContext,
        refetchCookieSession: refetchCookieSession,
        hasLicencePermission: hasLicencePermission
    }

    return (
        <AuthenticationContext.Provider value={authenticationContextValue}>
            {children}
        </AuthenticationContext.Provider>
    );
};

export default AuthenticationContextProvider;